import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useGetConfigurationData } from '../../../hooks/useConfigurationData';
import { client as publicClientApplication } from '../../../utilities/AuthUtils';
import { DateToLocaleString, longDateAndTimeFormat } from '../../../utilities/DateUtils';
import PackageNumbersField from './QIRFields/PackageNumberField';
import MultiselectBox from '../../Components/MultiselectBox';

function QIRDetails({ action, qir, onChange, businessUnitDetails }) {
    const { t } = useTranslation();
    let search = window.location.search;
    let params = new URLSearchParams(search);
    const account = publicClientApplication.getAllAccounts()[0];
    const { configurationData } = useGetConfigurationData();
    const [selectedCategories, setSelectedCategories] = useState(qir.categories ?? []);

    // Set lotNumber from query param 'lotNumber'
    const lotNumberQueryParam = params.get('lotNumber');
    if (action === "create" && lotNumberQueryParam) {
        qir.lotNumber = lotNumberQueryParam;
    }

    var selectedGradeId;
    // Set grade from query param 'grade'
    // Or if gradeId already exists on the QIR (e.g. update QIR)
    const gradeQueryParam = params.get('grade');
    if (action === "create" && gradeQueryParam) {
        let selectedGrade = businessUnitDetails?.grades.find(g => g.name.toUpperCase() === gradeQueryParam.toUpperCase());
        if (selectedGrade) {
            selectedGradeId = selectedGrade.id;
            qir.gradeId = selectedGradeId;
        }
    } else {
        selectedGradeId = qir.gradeId;
    }

    const [lotNumberFormat, setLotNumberFormat] = useState();

    // Get config to validate LotNumber
    useEffect(() => {
        if (configurationData && configurationData.length > 0 && (qir.businessUnitId || qir.siteId)) {

            let lotNumberFormatConfig = configurationData.filter(x => x.configurationDataPropertyName === "LotNumberFormat");
            if (lotNumberFormatConfig) {
                var lotNumberFormatConfigValue = lotNumberFormatConfig.find(x => x.businessUnitId != null && (x.businessUnitId === parseInt(qir.businessUnitId)));
                if (!lotNumberFormatConfigValue && qir.siteId) {
                    console.log(`LotNumberFormat config not found for business unit ${qir.businessUnitId}. Searching by site ${qir.siteId}`);
                    lotNumberFormatConfigValue = lotNumberFormatConfig.find(x => x.siteId != null && (x.siteId === qir.siteId));
                }

                if (lotNumberFormatConfigValue) {
                    console.log(`Found LotNumberFormat ${lotNumberFormatConfigValue.value}`);
                    setLotNumberFormat(lotNumberFormatConfigValue.value);
                }
                else {
                    console.log(`LotNumberFormat config not found for site ${qir.siteId} or business unit ${qir.businessUnitId}`);
                }
            }
            else {
                console.log("LotNumberFormat config not found");
            }
        }

    }, [qir, configurationData]);

    useEffect(() => {
        qir.categories = selectedCategories;
    }, [qir, selectedCategories])

    return (
        <Card className="mb-2">
            <Card.Header>{t('QIRDetail.Sections.Details')}</Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="lot">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.ClientQIRId')}</Form.Label>
                            <Col sm={9}>
                                <Form.Control type="text" size="sm" value={qir.clientQIRId && qir.clientQIRId} name="clientQIRId" onChange={onChange} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="lot">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.Lot')}</Form.Label>
                            <Col sm={9}>
                                <Form.Control type="text" size="sm" value={qir.lotNumber && qir.lotNumber} name="lotNumber" onChange={onChange} pattern={lotNumberFormat ? lotNumberFormat : "^[A-Z0-9]*$"} />
                            </Col>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="affectedAreaId">
                            <Form.Label column sm={3} className="required">{t('QIRDetail.Fields.AffectedArea')}</Form.Label>
                            <Col sm={9}>
                                <Form.Select required value={qir.affectedAreaId && qir.affectedAreaId} size="sm" name="affectedAreaId" onChange={onChange}>
                                    <option key="0" value="">{t('Application.Controls.DefaultSelect')}</option>
                                    {
                                        businessUnitDetails?.affectedAreas && businessUnitDetails.affectedAreas.map((affectedArea, i) => {
                                            return <option key={i + 1} value={affectedArea.id}>{affectedArea.display}</option>;
                                        })
                                    }
                                </Form.Select>
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <PackageNumbersField qir={qir} onChange={onChange} />
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="otherArea">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.OtherArea')}</Form.Label>
                            <Col sm={9}>
                                <Form.Control type="text" size="sm" value={qir.otherArea ?? ""} name="otherArea" onChange={onChange} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="gradeId">
                            <Form.Label column sm={3} className="required">{t('QIRDetail.Fields.Grade')}</Form.Label>
                            <Col sm={9}>
                                <Form.Select required value={selectedGradeId} size="sm" name="gradeId" onChange={onChange}>
                                    <option key="0" value="">{t('Application.Controls.DefaultSelect')}</option>
                                    {
                                        businessUnitDetails?.grades && businessUnitDetails.grades.map((grade, i) => {
                                            return <option key={i + 1} value={grade.id}>{grade.name}</option>;
                                        })
                                    }
                                </Form.Select>
                            </Col>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="categoryId">
                            <Form.Label column sm={3} className="required">{t('QIRDetail.Fields.Category')}</Form.Label>
                            <Col sm={9}>
                                <MultiselectBox
                                    options={businessUnitDetails?.categories}
                                    selectedOptions={selectedCategories}
                                    setSelectedOptions={setSelectedCategories}
                                    map="display" />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="originator">
                            <Form.Label column sm={3} className="required">{t('QIRDetail.Fields.Originator')}</Form.Label>
                            <Col sm={9}>
                                <Form.Control required type="text" size="sm" value={action === "create" ? account.username : qir.createdBy && qir.createdBy} name="originator" onChange={onChange} readOnly />
                            </Col>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="dueDate">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.DueDate')}</Form.Label>
                            <Col sm={9}>
                                {(action === "create" || action === "update") &&
                                    <Form.Control type="datetime-local" size="sm" value={qir.dueDate ?? ""} name="dueDate" onChange={onChange} />
                                }
                                {action === "view" &&
                                    <Form.Control type="text" size="sm" value={qir.dueDate && DateToLocaleString(qir.dueDate, longDateAndTimeFormat)} name="dueDate" onChange={onChange} readOnly />
                                }
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>

            </Card.Body>
        </Card>
    );
}

export default QIRDetails;